import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, FloppyDisk, Trash, Camera, Plus } from "@phosphor-icons/react";

const FLOORS = ["1st Floor", "2nd Floor", "3rd Floor", "Basement", "Warehouse", "Other"];

function Field({ label, children, mono = false, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">{label}</label>
      <div className={mono ? "font-mono" : ""}>{children}</div>
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623] transition-all";

export default function InventoryForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [showAddCat, setShowAddCat] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    name: "", sku: "", category: "", quantity: 1, location: "", floor: "",
    photo: "", purchase_date: "", purchase_price: "", purchase_vendor: "",
    end_of_life: "", ip_address: "", serial_number: "", notes: "",
    last_maintenance: "", next_maintenance: "", maintenance_interval_days: "",
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      const [{ data: cats }] = await Promise.all([api.get("/categories")]);
      setCategories(cats);
      if (isEdit) {
        try {
          const { data } = await api.get(`/inventory/${id}`);
          setForm({
            name: data.name || "", sku: data.sku || "", category: data.category || "",
            quantity: data.quantity ?? 1, location: data.location || "", floor: data.floor || "",
            photo: data.photo || "", purchase_date: data.purchase_date || "",
            purchase_price: data.purchase_price ?? "", purchase_vendor: data.purchase_vendor || "",
            end_of_life: data.end_of_life || "", ip_address: data.ip_address || "",
            serial_number: data.serial_number || "", notes: data.notes || "",
            last_maintenance: data.last_maintenance || "",
            next_maintenance: data.next_maintenance || "",
            maintenance_interval_days: data.maintenance_interval_days ?? "",
          });
        } catch (e) { setErr(formatApiError(e)); }
      } else if (!form.sku) {
        setField("sku", "KG-" + Math.random().toString(36).substring(2, 8).toUpperCase());
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr("");
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Auto-resize/compress so any phone photo fits comfortably
        const MAX_EDGE = 1600;
        let { width, height } = img;
        if (width > MAX_EDGE || height > MAX_EDGE) {
          const ratio = Math.min(MAX_EDGE / width, MAX_EDGE / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        // Try progressively lower quality until result fits ~800KB
        let quality = 0.85;
        let dataUrl = canvas.toDataURL("image/jpeg", quality);
        while (dataUrl.length > 800 * 1024 && quality > 0.4) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", quality);
        }
        setField("photo", dataUrl);
      };
      img.onerror = () => setErr("Couldn't read that image. Try another file.");
      img.src = reader.result;
    };
    reader.onerror = () => setErr("Couldn't read that file.");
    reader.readAsDataURL(file);
  };

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/categories", { name });
      setCategories((c) => [...c, data].sort((a, b) => a.name.localeCompare(b.name)));
      setField("category", data.name);
      setNewCategory(""); setShowAddCat(false);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      const payload = { ...form };
      // Normalize numeric/empty fields
      payload.quantity = Number(payload.quantity) || 1;
      payload.purchase_price = payload.purchase_price === "" ? null : Number(payload.purchase_price);
      payload.maintenance_interval_days = payload.maintenance_interval_days === "" ? null : Number(payload.maintenance_interval_days);
      // Convert date fields from YYYY-MM-DD to ISO strings; keep as-is if empty
      ["purchase_date", "end_of_life", "last_maintenance", "next_maintenance"].forEach((k) => {
        if (payload[k]) {
          // Store as ISO at midnight UTC to keep sortable strings
          if (payload[k].length === 10) payload[k] = payload[k] + "T00:00:00+00:00";
        } else {
          payload[k] = null;
        }
      });
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });

      if (isEdit) {
        await api.put(`/inventory/${id}`, payload);
        nav(`/inventory/${id}`);
      } else {
        const { data } = await api.post("/inventory", payload);
        nav(`/inventory/${data.id}`);
      }
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this asset? This cannot be undone.")) return;
    await api.delete(`/inventory/${id}`);
    nav("/inventory");
  };

  const displayDate = (v) => (v ? v.substring(0, 10) : "");

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-up" data-testid="inventory-form">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-[#F5A623] transition-colors">
        <ArrowLeft size={14} /> Back
      </button>

      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5A623]">
          {isEdit ? "Edit" : "New"} Asset
        </div>
        <h1 className="mt-2 text-4xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          {isEdit ? form.name || "Edit Asset" : "Register Asset"}
        </h1>
      </div>

      {err && <div className="text-sm text-red-400 bg-red-950/30 border border-red-900/50 rounded-md px-3 py-2">{err}</div>}

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Identity */}
          <section className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Identity</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name" testid="field-name">
                <input className={inputCls} value={form.name} onChange={(e) => setField("name", e.target.value)} required />
              </Field>
              <Field label="SKU" mono testid="field-sku">
                <input className={inputCls + " font-mono"} value={form.sku} onChange={(e) => setField("sku", e.target.value)} required />
              </Field>
              <Field label="Category" testid="field-category">
                <div className="flex gap-2">
                  <select className={inputCls} value={form.category} onChange={(e) => setField("category", e.target.value)} required>
                    <option value="">Select…</option>
                    {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                  <button type="button" onClick={() => setShowAddCat((v) => !v)} className="shrink-0 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 text-xs" data-testid="toggle-new-category">
                    <Plus size={14} />
                  </button>
                </div>
                {showAddCat && (
                  <div className="mt-2 flex gap-2">
                    <input className={inputCls} placeholder="New category name" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} data-testid="new-category-input" />
                    <button type="button" onClick={addCategory} className="px-3 rounded-md bg-[#F5A623] text-zinc-950 text-xs font-bold" data-testid="new-category-save">Add</button>
                  </div>
                )}
              </Field>
              <Field label="Quantity" testid="field-quantity">
                <input type="number" min="1" className={inputCls + " font-mono"} value={form.quantity} onChange={(e) => setField("quantity", e.target.value)} required />
              </Field>
              <Field label="Location / Room" testid="field-location">
                <input className={inputCls} placeholder="Studio A, Rack #2…" value={form.location} onChange={(e) => setField("location", e.target.value)} required />
              </Field>
              <Field label="Floor" testid="field-floor">
                <select className={inputCls} value={form.floor} onChange={(e) => setField("floor", e.target.value)}>
                  <option value="">Select…</option>
                  {FLOORS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
              <Field label="Serial Number" mono testid="field-serial">
                <input className={inputCls + " font-mono"} value={form.serial_number} onChange={(e) => setField("serial_number", e.target.value)} />
              </Field>
              <Field label="IP Address (if applicable)" mono testid="field-ip">
                <input className={inputCls + " font-mono"} placeholder="192.168.1.10" value={form.ip_address} onChange={(e) => setField("ip_address", e.target.value)} />
              </Field>
            </div>
          </section>

          {/* Lifecycle */}
          <section className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Purchase & Lifecycle</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Purchase Date" testid="field-purchase-date">
                <input type="date" className={inputCls} value={displayDate(form.purchase_date)} onChange={(e) => setField("purchase_date", e.target.value)} />
              </Field>
              <Field label="Purchase Price (USD)" testid="field-purchase-price">
                <input type="number" step="0.01" className={inputCls + " font-mono"} value={form.purchase_price} onChange={(e) => setField("purchase_price", e.target.value)} />
              </Field>
              <Field label="Vendor" testid="field-vendor">
                <input className={inputCls} value={form.purchase_vendor} onChange={(e) => setField("purchase_vendor", e.target.value)} />
              </Field>
              <Field label="End of Life" testid="field-eol">
                <input type="date" className={inputCls} value={displayDate(form.end_of_life)} onChange={(e) => setField("end_of_life", e.target.value)} />
              </Field>
            </div>
          </section>

          {/* Maintenance */}
          <section className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Maintenance Schedule</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Last Maintenance" testid="field-last-maintenance">
                <input type="date" className={inputCls} value={displayDate(form.last_maintenance)} onChange={(e) => setField("last_maintenance", e.target.value)} />
              </Field>
              <Field label="Next Maintenance" testid="field-next-maintenance">
                <input type="date" className={inputCls} value={displayDate(form.next_maintenance)} onChange={(e) => setField("next_maintenance", e.target.value)} />
              </Field>
              <Field label="Interval (days)" testid="field-interval">
                <input type="number" className={inputCls + " font-mono"} placeholder="e.g. 180" value={form.maintenance_interval_days} onChange={(e) => setField("maintenance_interval_days", e.target.value)} />
              </Field>
            </div>
          </section>

          {/* Notes */}
          <section className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Notes</div>
            <textarea rows={4} className={inputCls} value={form.notes} onChange={(e) => setField("notes", e.target.value)} data-testid="field-notes" />
          </section>
        </div>

        {/* Right — Photo */}
        <div className="lg:col-span-1 space-y-6">
          <section className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Photo</div>
            <div className="aspect-square rounded-md overflow-hidden bg-zinc-950 border border-zinc-800 grid place-items-center">
              {form.photo
                ? <img src={form.photo} alt="preview" className="h-full w-full object-cover" />
                : <Camera size={40} className="text-zinc-700" weight="duotone" />}
            </div>
            <label className="inline-flex items-center justify-center gap-2 w-full px-3 py-2 rounded-md bg-zinc-800 border border-zinc-700 text-sm text-zinc-300 hover:text-white hover:bg-zinc-700 cursor-pointer transition-colors">
              <Camera size={14} /> {form.photo ? "Replace Photo" : "Upload / Take Photo"}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhoto} data-testid="photo-input" />
            </label>
            {form.photo && (
              <button type="button" onClick={() => setField("photo", "")} className="text-xs text-red-400 hover:underline">
                Remove photo
              </button>
            )}
          </section>

          <div className="flex flex-col gap-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="save-item-btn"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#F5A623] hover:bg-[#E67E22] text-zinc-950 font-bold text-sm transition-colors disabled:opacity-60"
            >
              <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Asset"}
            </button>
            {isEdit && (
              <button
                type="button" onClick={onDelete}
                data-testid="delete-item-btn"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-zinc-900 border border-red-900/50 text-red-400 hover:bg-red-950/30 text-sm transition-colors"
              >
                <Trash size={16} /> Delete Asset
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
